import React from 'react';
import { Icon, Popover } from './components.jsx';
// L&T IT Helpdesk - Feedback / Platform Requests


  const { useState, useEffect, useCallback, useRef, useMemo } = React;

  // ─── Constants ───────────────────────────────────────────────────────────────

  const MANAGER_IDS = ['james', 'rob'];

  const FB_CATEGORIES = [
    'New Feature', 'UI/UX', 'Performance', 'Bug Report', 'Integration', 'Other',
  ];

  const FB_STATUSES = [
    { id: 'pending',     label: 'Submitted',   color: '#64748B', bg: '#F1F5F9' },
    { id: 'reviewing',   label: 'Reviewing',   color: '#F59E0B', bg: '#FFFBEB' },
    { id: 'planned',     label: 'Planned',     color: '#8B5CF6', bg: '#F5F3FF' },
    { id: 'in-progress', label: 'In Progress', color: '#3B82F6', bg: '#EFF6FF' },
    { id: 'completed',   label: 'Completed',   color: '#22C55E', bg: '#F0FDF4' },
    { id: 'declined',    label: 'Declined',    color: '#EF4444', bg: '#FEF2F2' },
  ];

  // Sticky status filter - persists across navigation + browser sessions via
  // localStorage. Default is everything selected; user toggles to narrow.
  const FB_FILTER_KEY = 'triage:feedback:filterStatuses';
  const DEFAULT_FILTER_STATUSES = FB_STATUSES.map(s => s.id);
  const loadStickyFilter = () => {
    try {
      const raw = localStorage.getItem(FB_FILTER_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.every(x => typeof x === 'string')) return parsed;
      }
    } catch (_) { /* corrupt storage - fall through to default */ }
    return DEFAULT_FILTER_STATUSES;
  };
  const saveStickyFilter = (ids) => {
    try { localStorage.setItem(FB_FILTER_KEY, JSON.stringify(ids)); } catch (_) { /* quota / private mode */ }
  };

  // Shared API caller - see window.IT_AUTH.api in index.html.
  const fbApi = (method, path, body) => window.IT_AUTH.api(method, path, body, 'fbApi');

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const fmtDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // ─── Status badge ─────────────────────────────────────────────────────────────

  const StatusBadge = ({ status }) => {
    const s = FB_STATUSES.find(x => x.id === status) || FB_STATUSES[0];
    return (
      <span style={{
        fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 99,
        color: s.color, background: s.bg, letterSpacing: '0.02em', whiteSpace: 'nowrap',
      }}>
        {s.label}
      </span>
    );
  };

  // ─── Category pill ────────────────────────────────────────────────────────────

  const CatBadge = ({ cat }) => (
    <span style={{
      fontSize: 11, padding: '1px 8px', borderRadius: 99,
      background: 'var(--bg)', border: '1px solid var(--line)',
      color: 'var(--text-subtle)', fontWeight: 500, whiteSpace: 'nowrap',
    }}>
      {cat}
    </span>
  );

  // ─── Request card ─────────────────────────────────────────────────────────────

  const RequestCard = ({ req, myId, isManager, onVote, onStatusChange, onStatusUpdate, onDelete }) => {
    const [notesOpen, setNotesOpen]   = useState(false);
    const [notes, setNotes]           = useState(req.adminNotes || '');
    const [savingNotes, setSaving]    = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    // Status-change modal - null when closed, or { newStatus, note, notify }
    // when open. Opens when the user picks a status from the popover.
    const [statusModal, setStatusModal] = useState(null);
    const [savingStatus, setSavingStatus] = useState(false);
    const statusBtnRef = useRef();

    const hasVoted  = req.voters && req.voters.includes(myId);
    const canDelete = isManager || req.submittedBy === myId;

    // resolve submitter display name from TASK_USERS if loaded
    const resolveUser = (id) => {
      if (window.TASK_USERS) {
        const u = window.TASK_USERS.find(u => u.id === id);
        if (u) return u.name;
      }
      return id;
    };

    const saveNotes = async () => {
      setSaving(true);
      try { await onStatusChange(req.id, { adminNotes: notes }); setNotesOpen(false); }
      catch (e) { alert('Save failed: ' + e.message); }
      finally { setSaving(false); }
    };

    return (
      <div style={{
        display: 'flex', gap: 14, padding: '14px 16px',
        background: 'var(--bg-elev)', border: '1px solid var(--line)',
        borderRadius: 8, marginBottom: 8,
      }}>

        {/* ── Vote column ── */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 2 }}>
          <button
            onClick={() => onVote(req.id)}
            title={hasVoted ? 'Remove vote' : 'Upvote this request'}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
              padding: '6px 10px', borderRadius: 7, cursor: 'pointer',
              border: hasVoted ? '1.5px solid var(--accent)' : '1px solid var(--line)',
              background: hasVoted ? 'var(--accent-soft)' : 'var(--bg)',
              color: hasVoted ? 'var(--accent)' : 'var(--text-subtle)',
              transition: 'all 0.15s', minWidth: 42,
            }}
          >
            <Icon name="arrowUp" size={14}/>
            <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1, marginTop: 1 }}>
              {req.voteCount || 0}
            </span>
          </button>
        </div>

        {/* ── Main content ── */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Title + status */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 5 }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', flex: 1, lineHeight: 1.4 }}>
              {req.title}
            </span>
            <StatusBadge status={req.status}/>
          </div>

          {/* Category + submitter */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <CatBadge cat={req.category}/>
            <span style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
              {resolveUser(req.submittedBy)} · {fmtDate(req.createdAt)}
            </span>
          </div>

          {/* Description */}
          {req.description && (
            <p style={{
              fontSize: 13, color: 'var(--text-muted)', margin: '0 0 6px',
              lineHeight: 1.55, whiteSpace: 'pre-wrap',
            }}>
              {req.description}
            </p>
          )}

          {/* Admin note (visible to all) */}
          {req.adminNotes && (
            <div style={{
              fontSize: 12, color: '#92400E', background: '#FEF3C7',
              border: '1px solid #FCD34D', borderRadius: 6,
              padding: '5px 10px', marginTop: 6, lineHeight: 1.5,
            }}>
              <strong>Admin note: </strong>{req.adminNotes}
            </div>
          )}

          {/* Manager controls */}
          {isManager && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                ref={statusBtnRef}
                className="btn"
                style={{ fontSize: 11, padding: '3px 9px' }}
                onClick={() => setStatusOpen(v => !v)}
              >
                Set status ▾
              </button>

              {statusOpen && (
                <Popover anchorRef={statusBtnRef} onClose={() => setStatusOpen(false)}>
                  {FB_STATUSES.map(s => (
                    <div
                      key={s.id}
                      className="pop-item"
                      style={{ fontSize: 12 }}
                      onClick={() => {
                        setStatusOpen(false);
                        // Picking the current status is a no-op - don't open the modal.
                        if (s.id === req.status) return;
                        // Open the modal so the IT user can add a note +
                        // choose whether to email the requester. The
                        // "Submitted" status doesn't trigger an email
                        // server-side, but the modal still opens for
                        // consistency (note-only update).
                        setStatusModal({
                          newStatus: s.id,
                          note: req.adminNotes || '',
                          notify: s.id !== 'pending',
                        });
                      }}
                    >
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: s.color, display: 'inline-block', marginRight: 7, flexShrink: 0,
                      }}/>
                      {s.label}
                    </div>
                  ))}
                </Popover>
              )}

              <button
                className="btn"
                style={{ fontSize: 11, padding: '3px 9px' }}
                onClick={() => { setNotesOpen(v => !v); setNotes(req.adminNotes || ''); }}
              >
                {req.adminNotes ? 'Edit note' : 'Add note'}
              </button>

              {canDelete && (
                <button
                  className="btn"
                  style={{ fontSize: 11, padding: '3px 9px', color: '#EF4444', borderColor: '#FECACA' }}
                  onClick={() => { if (window.confirm('Delete this request?')) onDelete(req.id); }}
                >
                  Delete
                </button>
              )}
            </div>
          )}

          {/* Non-manager own-request delete */}
          {!isManager && req.submittedBy === myId && (
            <div style={{ marginTop: 8 }}>
              <button
                className="btn"
                style={{ fontSize: 11, padding: '3px 9px', color: '#EF4444', borderColor: '#FECACA' }}
                onClick={() => { if (window.confirm('Delete your request?')) onDelete(req.id); }}
              >
                Delete
              </button>
            </div>
          )}

          {/* Admin note editor */}
          {notesOpen && (
            <div style={{ marginTop: 8 }}>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add a note visible to all users (e.g. 'This is planned for Q3')…"
                style={{
                  width: '100%', padding: '6px 8px', fontSize: 12,
                  border: '1px solid var(--line)', borderRadius: 6,
                  background: 'var(--bg)', color: 'var(--text)',
                  resize: 'vertical', minHeight: 64, boxSizing: 'border-box',
                  fontFamily: 'inherit', lineHeight: 1.5,
                }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
                <button
                  className="btn primary"
                  style={{ fontSize: 11 }}
                  onClick={saveNotes}
                  disabled={savingNotes}
                >
                  {savingNotes ? 'Saving…' : 'Save note'}
                </button>
                <button
                  className="btn"
                  style={{ fontSize: 11 }}
                  onClick={() => setNotesOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Status-change modal - opens when IT picks a new status from
            the dropdown. Captures (status, admin note, email-or-not) in one
            shot so the server can write the DB row + send the requester
            email atomically. */}
        {statusModal && (() => {
          const targetStatus = FB_STATUSES.find(s => s.id === statusModal.newStatus);
          if (!targetStatus) return null;
          const canEmail = statusModal.newStatus !== 'pending';
          const submit = async () => {
            setSavingStatus(true);
            try {
              await onStatusUpdate(req.id, {
                status: statusModal.newStatus,
                adminNotes: statusModal.note,
                notifyRequester: canEmail && statusModal.notify,
              });
              setStatusModal(null);
            } finally {
              setSavingStatus(false);
            }
          };
          return (
            <div
              className="modal-wrap"
              onClick={() => !savingStatus && setStatusModal(null)}
            >
              <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 540 }}>
                <div className="modal-head">
                  <h2>Update status</h2>
                  <button className="btn ghost" style={{ marginLeft: 'auto' }} onClick={() => setStatusModal(null)} disabled={savingStatus}>
                    <Icon name="close" size={11}/>
                  </button>
                </div>
                <div className="modal-body">
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Request</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{req.title}</div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>New status</div>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: targetStatus.bg, color: targetStatus.color,
                    }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: targetStatus.color }}/>
                      {targetStatus.label}
                    </span>
                  </div>

                  <div className="field">
                    <label>Admin note</label>
                    <textarea
                      value={statusModal.note}
                      onChange={e => setStatusModal(m => ({ ...m, note: e.target.value }))}
                      placeholder="Add context for the requester (and the rest of the team). Visible on the request card."
                      style={{ minHeight: 100, fontFamily: 'inherit' }}
                      autoFocus
                    />
                  </div>

                  <label
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '12px 14px', marginTop: 8,
                      border: '1px solid var(--line)', borderRadius: 6,
                      cursor: canEmail ? 'pointer' : 'not-allowed',
                      background: (canEmail && statusModal.notify) ? 'var(--bg-sub)' : 'transparent',
                      opacity: canEmail ? 1 : 0.55,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={canEmail && statusModal.notify}
                      disabled={!canEmail}
                      onChange={e => setStatusModal(m => ({ ...m, notify: e.target.checked }))}
                      style={{ marginTop: 3 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        Email the requester
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                        {canEmail ? (
                          <>Sends a "Status: {targetStatus.label}" email from <code>triage@leeandthompson.com</code> with your admin note included.</>
                        ) : (
                          <>Email is disabled when status is set back to <strong>Submitted</strong> (internal-only change).</>
                        )}
                      </div>
                    </div>
                  </label>
                </div>
                <div className="modal-foot">
                  <button className="btn" onClick={() => setStatusModal(null)} disabled={savingStatus}>Cancel</button>
                  <button className="btn primary" onClick={submit} disabled={savingStatus}>
                    {savingStatus ? 'Saving…' : (canEmail && statusModal.notify ? 'Update & email' : 'Update')}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // ─── Submit modal ─────────────────────────────────────────────────────────────

  const SubmitModal = ({ onClose, onSubmit }) => {
    const [title, setTitle]           = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory]     = useState('New Feature');
    const [submitting, setSubmitting] = useState(false);

    const doSubmit = async () => {
      if (!title.trim()) return;
      setSubmitting(true);
      try {
        await onSubmit({ title: title.trim(), description: description.trim(), category });
        onClose();
      } catch (e) {
        alert('Failed to submit: ' + e.message);
        setSubmitting(false);
      }
    };

    const onKey = (e) => { if (e.key === 'Enter' && e.ctrlKey) doSubmit(); };

    return (
      <div className="modal-wrap">
        {/* Backdrop click does NOT close - use the X button / Cancel to avoid losing data. */}
        <div className="modal" style={{ width: 560, maxWidth: '94vw' }}>
          <div className="modal-head">
            <h2>Submit a platform request</h2>
            <button className="btn ghost" style={{ marginLeft: 'auto' }} onClick={onClose}>
              <Icon name="close" size={11}/>
            </button>
          </div>
          <div className="modal-body">
            <div className="field">
              <label>What would you like?<span style={{ color: '#EF4444', marginLeft: 2 }}>*</span></label>
              <input
                autoFocus
                value={title}
                onChange={e => setTitle(e.target.value)}
                onKeyDown={onKey}
                placeholder="e.g. Add a dark mode toggle to the sidebar"
              />
            </div>
            <div className="field">
              <label>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}>
                {FB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Description <span style={{ fontWeight: 400, color: 'var(--text-subtle)' }}>(optional)</span></label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                onKeyDown={onKey}
                placeholder="Describe the change, the problem it solves, or any examples…"
                style={{ minHeight: 96 }}
              />
            </div>
          </div>
          <div className="modal-foot">
            <span style={{ marginRight: 'auto', fontSize: 11, color: 'var(--text-subtle)' }}>
              Ctrl+Enter to submit
            </span>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button
              className="btn primary"
              onClick={doSubmit}
              disabled={!title.trim() || submitting}
            >
              {submitting ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Main view ────────────────────────────────────────────────────────────────

  const FeedbackView = () => {
    const myId      = window.ME_ID || '';
    const isManager = MANAGER_IDS.includes(myId);

    const [requests, setRequests] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [submitOpen, setSubmit] = useState(false);
    const [sortBy, setSortBy]     = useState('newest');    // newest | votes
    // Multi-select status filter - array of status ids. Lazy init reads from
    // localStorage; persists on every toggle so navigating away and back keeps
    // the same selection.
    const [filterStatuses, setFilterStatuses] = useState(loadStickyFilter);
    const toggleStatusFilter = useCallback((id) => {
      setFilterStatuses(prev => {
        const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
        saveStickyFilter(next);
        return next;
      });
    }, []);
    const setAllStatuses = useCallback(() => {
      const next = FB_STATUSES.map(s => s.id);
      saveStickyFilter(next);
      setFilterStatuses(next);
    }, []);
    const clearAllStatuses = useCallback(() => {
      saveStickyFilter([]);
      setFilterStatuses([]);
    }, []);
    const [filterCat, setFC]      = useState('all');

    const load = useCallback(async () => {
      try {
        const data = await fbApi('GET', '/feedback');
        setRequests(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn('[Feedback] load failed:', e.message);
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Vote toggle ──
    const handleVote = useCallback(async (id) => {
      // Optimistic
      setRequests(prev => prev.map(r => {
        if (r.id !== id) return r;
        const voted = r.voters && r.voters.includes(myId);
        return {
          ...r,
          voteCount: voted ? r.voteCount - 1 : (r.voteCount || 0) + 1,
          voters: voted ? r.voters.filter(v => v !== myId) : [...(r.voters || []), myId],
        };
      }));
      try {
        const updated = await fbApi('POST', `/feedback/${id}/vote`, { userId: myId });
        setRequests(prev => prev.map(r => r.id === id ? updated : r));
      } catch (e) {
        console.warn('[Feedback] vote failed:', e.message);
        load();
      }
    }, [myId, load]);

    // ── Status / notes change ──
    const handleStatusChange = useCallback(async (id, changes) => {
      try {
        const updated = await fbApi('PATCH', `/feedback/${id}`, changes);
        setRequests(prev => prev.map(r => r.id === id ? updated : r));
      } catch (e) {
        alert('Update failed: ' + e.message);
      }
    }, []);

    // Dedicated status-update path. Uses /feedback/{id}/status-update so the
    // server can atomically save status + note + send the requester email.
    // Modal in RequestCard collects status, note, and notify-checkbox state.
    const handleStatusUpdate = useCallback(async (id, { status, adminNotes, notifyRequester }) => {
      try {
        const updated = await fbApi('POST', `/feedback/${id}/status-update`, {
          status, adminNotes, notifyRequester,
        });
        setRequests(prev => prev.map(r => r.id === id ? updated : r));
      } catch (e) {
        alert('Update failed: ' + e.message);
      }
    }, []);

    // ── Delete ──
    const handleDelete = useCallback(async (id) => {
      try {
        await fbApi('DELETE', `/feedback/${id}`);
        setRequests(prev => prev.filter(r => r.id !== id));
      } catch (e) {
        alert('Delete failed: ' + e.message);
      }
    }, []);

    // ── Submit new ──
    const handleSubmit = useCallback(async (data) => {
      const created = await fbApi('POST', '/feedback', { ...data, submittedBy: myId });
      setRequests(prev => [created, ...prev]);
    }, [myId]);

    // ── Filtered + sorted list ──
    const filtered = useMemo(() => {
      let list = requests;
      // Empty status array = show nothing (user can hit "All" to reset).
      // Selected array = show only requests matching those statuses.
      list = list.filter(r => filterStatuses.includes(r.status));
      if (filterCat !== 'all') list = list.filter(r => r.category === filterCat);
      const sorted = list.slice();
      if (sortBy === 'votes') sorted.sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0));
      else sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return sorted;
    }, [requests, filterStatuses, filterCat, sortBy]);

    const totalCount = requests.length;

    return (
      // Two-layer wrapper so the scrollbar sits at the right edge of the
      // main pane, not in the middle of the page next to the 820px column.
      // Outer = full width, scrollable; inner = centred 820px content.
      <div style={{ height: '100%', overflowY: 'auto' }}>
        <div style={{ padding: '24px 28px', maxWidth: 820, margin: '0 auto', boxSizing: 'border-box' }}>

        {/* ── Page header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 22 }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>
              Platform Requests
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-subtle)', lineHeight: 1.5 }}>
              Submit ideas and vote on changes you'd like to see in the IT Helpdesk.
              {totalCount > 0 && <span style={{ marginLeft: 6 }}>{totalCount} request{totalCount !== 1 ? 's' : ''} total.</span>}
            </p>
          </div>
          <button className="btn primary" onClick={() => setSubmit(true)}>
            <Icon name="plus" size={11}/> New request
          </button>
        </div>

        {/* ── Filters + sort ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {/* Status pills: multi-select, sticky via localStorage */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 4 }}>Status</span>
            {FB_STATUSES.map(s => {
              const active = filterStatuses.includes(s.id);
              return (
                <button
                  key={s.id}
                  onClick={() => toggleStatusFilter(s.id)}
                  style={{
                    fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 12,
                    border: `1px solid ${active ? s.color : 'var(--line)'}`,
                    background: active ? s.bg : 'transparent',
                    color: active ? s.color : 'var(--text-muted)',
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  {s.label}
                </button>
              );
            })}
            <span style={{ flex: 1 }}/>
            <button
              onClick={setAllStatuses}
              style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            >All</button>
            <button
              onClick={clearAllStatuses}
              style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            >None</button>
          </div>

          {/* Category + sort row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={filterCat}
              onChange={e => setFC(e.target.value)}
              style={{ fontSize: 12, padding: '5px 8px', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--bg-elev)', color: 'var(--text)', cursor: 'pointer' }}
            >
              <option value="all">All categories</option>
              {FB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
              {[['newest', 'Newest'], ['votes', 'Most voted']].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setSortBy(id)}
                  style={{
                    fontSize: 11, padding: '4px 11px', borderRadius: 6,
                    border: sortBy === id ? 'none' : '1px solid var(--line)',
                    background: sortBy === id ? 'var(--accent)' : 'var(--bg-elev)',
                    color: sortBy === id ? '#fff' : 'var(--text)',
                    cursor: 'pointer', fontWeight: sortBy === id ? 600 : 400,
                    transition: 'all 0.15s',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>{/* close Category + sort row */}
        </div>{/* close outer filters column */}

        {/* ── Request list ── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-subtle)', fontSize: 13 }}>
            Loading requests…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: 56, color: 'var(--text-subtle)', fontSize: 13,
            border: '1px dashed var(--line)', borderRadius: 8,
          }}>
            {requests.length === 0
              ? <><div style={{ fontSize: 28, marginBottom: 8 }}>💬</div>No requests yet - be the first to submit one!</>
              : 'No requests match the current filters.'}
          </div>
        ) : (
          filtered.map(req => (
            <RequestCard
              key={req.id}
              req={req}
              myId={myId}
              isManager={isManager}
              onVote={handleVote}
              onStatusChange={handleStatusChange}
              onStatusUpdate={handleStatusUpdate}
              onDelete={handleDelete}
            />
          ))
        )}

        {/* ── Submit modal ── */}
        {submitOpen && (
          <SubmitModal
            onClose={() => setSubmit(false)}
            onSubmit={handleSubmit}
          />
        )}
        </div>
      </div>
    );
  };

export { FeedbackView };